import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBackend } from '../context/BackendContext';
import './Login.css';
import '../App.css';
import exp8Logo from '../svg/WebClient-Express8-Logo-Text-Web.svg';
import aaxLogo from '../svg/WebClient-ArchivX-Logo-Text-Web.svg';
import webClientLogo from '../svg/WebClient-Logo-Text-Web.svg';
import augiasLogo from '../svg/AUGIAS-DATA-Logo-Einzeiler-weiss-Web.svg';

import { LoginProps } from '../interfaces';

function Login() {
  const { login } = useAuth();
  const backend = useBackend();
  const navigate = useNavigate();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [release, setRealease] = useState('');
  const [serverError, setServerError] = useState<boolean>(false);

  const [passwordType, setPasswordType] = useState('password');
  const [passwordError, setPasswordError] = useState('login-pass-error-hidden');

  const passwordInputRef = useRef<HTMLInputElement>(null);
  const userInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const url = '/webclient/php/get_release.php';
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Anfrage fehlgeschlagen');
        }
        return res.json();
      })
      .then((data) => {
        setRealease(data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, []);

  function handleChangeUser(event:React.ChangeEvent<HTMLInputElement>) {
    setUser(event.currentTarget.value);
  }

  function handleChangePassword(event:React.ChangeEvent<HTMLInputElement>) {
    setPassword(event.currentTarget.value);
  }

  function handleSubmit(event:React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const url = '/webclient/php/login.php';
    const form = new FormData(document.getElementById('login') as HTMLFormElement ?? undefined);

    fetch(url, { method: 'POST', body: form })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Anfrage fehlgeschlagen');
        }
        return res.json();
      })
      .then((data) => {
        if (data.error === 'no-response-error') {
          setError(data.error);

          setPasswordError('login-no-response-error-visible');          
        } else if (data.error === 'user-or-password-error') {
          setError(data.error);

          setPasswordError('login-user-or-pass-error-visible');

          userInputRef.current?.focus();
        } else {
          login();
          navigate('/bestandsregie');
        }
      })
      .catch((err) => {
        console.log(err.message);
        setServerError(true);
      });
  }
  
  if (backend === 0) {
    return <div>Die Konfiguration der Datenbank-Anbindung ist unvollständig.</div>;
  }

  if (serverError) {
    return <div>Internal Server Error</div>;
  }

  return (
    <div className="login-container">
      <header role="banner" className="login-header" />

      <main className="login-main">
        <form 
          aria-label="Anmeldemenü"
          id="login"
          className="login-form" 
          onSubmit={handleSubmit}
        >
          <div className="login-logo">
            <img 
              className="" 
              src={webClientLogo} 
              alt="Logo WebClient" 
            />
          </div>
          <div className="login-name">
            <label htmlFor="user">Login Anmeldename</label>
            <input 
              type="text" 
              aria-describedby={error === 'user-error' ? 'user-error' : ''}
              aria-invalid={error === 'user-error'}
              className={error === 'user-error' ? 'login-pass-input-error' : 'login-pass-input'} 
              ref={userInputRef} 
              id="user" 
              name="user" 
              value={user} 
              onChange={handleChangeUser} 
            />
            <div 
              role="alert" 
              id="user-or-password-error"
              className={error === 'user-or-password-error' ? passwordError : 'display-none'}
            >
              Der Benutzername oder das Passwort ist nicht korrekt.
            </div>
          </div>
          <div className="login-pass">
            <label htmlFor="login_input_pwd">Passwort</label>
            <div className="login-pass-input-form">
              <input 
                aria-describedby={error === 'password-error' ? 'password-error' : ''}
                aria-invalid={error === 'password-error'}
                className={error === 'password-error' ? 'login-pass-input-error' : 'login-pass-input'} 
                ref={passwordInputRef} 
                id="login_input_pwd" 
                type={passwordVisible ? 'text' : 'password'} 
                name="password" 
                value={password} 
                onChange={handleChangePassword} 
              />
              <button 
                type="button" 
                aria-label={passwordVisible ? 'Klartext ausblenden' : 'Klartext anzeigen'}
                className={passwordVisible ? 'eye-off' : 'eye'} 
                onClick={() => setPasswordVisible(!passwordVisible)}
              />
            </div>
            <div>Sie haben Ihr Passwort vergessen? Dann kontaktieren Sie bitte Ihren Anwendungsbetreuer.</div>
          </div>
          <div />
          <div className="login-button">
            <button type="submit" aria-label="anmeldedaten senden">OK</button>
          </div>  
          <div 
            role="alert" 
            id="no-response-error"
            className={error === 'no-response-error' ? passwordError : 'display-none'}
          >
            Der Server antwortet nicht.
          </div>           
        </form>
      </main>
      
      <footer role="contentinfo" className="login-footer">
        <p>Release: {release}</p>
        <img aria-hidden="true" className="" src={augiasLogo} alt="" />
      </footer>
    </div>
  );
}

export default Login;
